import { OrderView, PageReference } from '@gkn-ili/views/src/OrderDocumentation';
import { getPageContent } from '../index';
import { isContentModel } from '@gkn-ili/views/dist/PageContent';
import { Instance3D, Model3DXML, Reference3D } from '@gkn-ili/cad-model';
import _ from 'lodash';

export function refApplies(personalRef: string[], reference: string[]): boolean {
  return reference.every((v, i) => (i < personalRef.length ? personalRef[i] === v : false));
}
export function refIsEqual(a: string[], b: string[]): boolean {
  a = [...a];
  b = [...b];

  if (a.length !== b.length) {
    return false;
  }
  return a.every((v, i) => v === b[i]);
}

type AppModelConfigurationState = { visible: boolean };

export type View3DConfigurationRule = { reference: string[]; visible: boolean; inheritanceLevel: number };
export type View3DActionRule =
  | { reference: string[]; state: 'active'; color: number }
  | { reference: string[]; state: 'animated'; offsetPosition: [number, number, number]; color: number };

const inheritanceBins = {
  all: [0, 1, 2],
  direct: [0],
  inherited: [1, 2],
};
export function getConfigurationRulesForPage(
  reference: PageReference,
  orderView: OrderView,
  inheritance: keyof typeof inheritanceBins = 'all',
): View3DConfigurationRule[] {
  function getRulesForReference(reference: PageReference, inheritanceLevel = 0): View3DConfigurationRule[] {
    const rules: View3DConfigurationRule[] = [];

    const page = getPageContent(orderView, reference);
    if (isContentModel(page)) {
      const model = page.content.model;
      rules.push(...model.configuration.modifiers.map((k) => ({ ...k, inheritanceLevel })));
    }

    return rules;
  }

  const inheritanceLevels = inheritanceBins[inheritance];

  return getRulesForReference(reference).filter((rule) => rule.inheritanceLevel in inheritanceLevels);
}

export function applyConfigurationRules(
  testRef: string[],
  rules: View3DConfigurationRule[],
): AppModelConfigurationState {
  function applyContent(current: AppModelConfigurationState, rule: View3DConfigurationRule) {
    if (refApplies(testRef, rule.reference)) {
      current.visible = rule.visible;
    }

    return current;
  }

  const result = rules.reduce<AppModelConfigurationState>((state, rule) => applyContent(state, rule), {
    visible: true,
  });

  return result;
}

export function applyActionRules(testRef: string[], actions: View3DActionRule[]) {
  return actions.reduce<View3DActionRule | { state: 'none' }>(
    (result, actionState) => {
      if (refApplies(testRef, actionState.reference)) {
        return actionState;
      }
      return result;
    },
    { state: 'none' },
  );
}

export function getByRef(model: Model3DXML, ref: string[]): Reference3D | Instance3D {
  function recurse(reference3D: Reference3D, refReverse: string[]): Reference3D | Instance3D {
    if (refReverse.length === 0) {
      return reference3D;
    }
    const instance = Object.values(reference3D.children).find((c) => c.name === refReverse[0]);
    if (!instance) {
      return reference3D;
    }
    if (refReverse.length === 1) {
      return instance;
    }
    return recurse(instance.instance, refReverse.slice(2));
  }

  return recurse(model, _.clone(ref).reverse());
}
